import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AuditLogDto } from "../../types/AuditLog";
import { format } from "date-fns";
import { GLOBALSETTINGS } from "../../data/constants";
import { formatDates } from "../../utils/dateHelper";
import useApi from "../../hooks/useApi";
import { getAuditLogDetails } from "../../services/auditLogService";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";

interface AuditLogDetailsProps {
  open: boolean;
  handleClose: () => void;
  selectedRow: AuditLogDto | null;
  source: string;
}

const AuditLogDetails: React.FC<AuditLogDetailsProps> = ({ open, handleClose, selectedRow, source }) => {
  const { loading, executeApi } = useApi();
  const [details, setDetails] = useState<AuditLogDto | null>(null);

  useEffect(() => {
    if (!open || !selectedRow) {
      setDetails(null);
      return;
    }

    (async () => {
      const result = await executeApi(getAuditLogDetails, { auditLogId: selectedRow.auditLogId, source });
      setDetails(result);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedRow?.auditLogId]);

  const displayedRow = details ?? selectedRow;
  const formatJson = (json) => {
    const parsed =
      typeof json === "string"
        ? JSON.parse(json.trim().startsWith("{") && json.includes("},{") ? `[${json}]` : json)
        : json;

    const dataArray = Array.isArray(parsed) ? parsed : [parsed];

    return dataArray
      .map((item) => {
        const displayData = formatDates(item);

        return Object.entries(displayData)
          .map(([key, value]) => {
            const val = value === null || value === undefined ? "" : value;
            return `${key}: ${val}`;
          })
          .join("\n");
      })
      .join("\n\n---\n\n");
  };

  const getLabelForKey = (key: string) => {
    const labels = {
      auditLogId: "Id",
      auditUser: "User",
      auditDate: "Date Time",
      eventType: "Event Type",
      moduleId: "Module Id",
      moduleName: "Module Name",
      primaryRecordId: "Primary Record Id",
      secondaryRecordId: "Secondary Record Id",
      summary: "Summary",
      details: "Details",
      oldRecordJson: "Old Record Json",
      newRecordJson: "New Record Json",
    };

    return labels[key] || key;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEnforceFocus
      aria-labelledby="customized-dialog-title"
    >
      <DialogTitle id="customized-dialog-title">Audit Log Details</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={() => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: "#fff",
        })}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers sx={{ position: "relative" }}>
        <BackdropProgressBar loading={loading} showAbsolute />
        {displayedRow && (
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {Object.entries(displayedRow)
                  .filter(([key]) => key !== "totalRecords")
                  .map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell style={{ width: "200px" }}>
                        <strong>{getLabelForKey(key)}</strong>
                      </TableCell>
                      <TableCell style={{ minHeight: "100px", verticalAlign: "middle" }}>
                        {(key === "newRecordJson" || key === "oldRecordJson") && value ? (
                          <pre style={{ marginBottom: 0 }}>{formatJson(value)}</pre>
                        ) : key.includes("Date") && value ? (
                          format(value, GLOBALSETTINGS.DATE_FORMAT_TIME)
                        ) : (
                          String(value ?? "")
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <Button id="btnClose" onClick={handleClose} autoFocus variant="contained" color="light">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogDetails;
